/* eslint-disable @typescript-eslint/no-inferrable-types */
import { BaseCommon, EquipmentCommon, MessageCommon, TextCommon } from '../common';
import { EMPTY_STRING, FileSupport, PATH_INDEX, ZERO_NUMBER } from 'src/common/base.common';
import { EFileExtensionCsv, EFileExtensionUpload, FileLog } from '../types';
import format from 'string-format';

const validationFile = async (dirHandle: any, name: string[] = [EquipmentCommon.ROOT_FILE]): Promise<string> => {
    for await (const entry of dirHandle.values()) {
        if (entry.kind === 'file') {
            const file = (await entry.getFile()) as FileLog;
            if (name.includes(file.name)) {
                return file.name;
            }
        }
    }
    throw new Error(MessageCommon.NOT_FOUND_EQUIPMENT_FILE);
};

const handleDirectoryEntry = async (dirHandle: any, outputs: FileLog[], name: string = ''): Promise<any> => {
    for await (const entry of dirHandle.values()) {
        if (entry.kind === 'file') {
            const file = (await entry.getFile()) as FileLog;
            Object.defineProperty(file, 'path', { value: [name, file.name].join(BaseCommon.CHARACTER_SLASH), writable: false });
            outputs.push(file);
        }

        if (entry.kind === 'directory') {
            const newHandle = await dirHandle.getDirectoryHandle(entry.name, { create: false });
            const path = [name, entry.name].join(BaseCommon.CHARACTER_SLASH);
            await handleDirectoryEntry(newHandle, outputs, path);
        }
    }
};

const filterFileAndFolder = async (
    outputs: FileLog[],
    filterFolder: string[] = [],
    filterFileExtensions: string[] = [],
    isCSVFormat: boolean = false,
): Promise<FileLog[]> => {
    let data: FileLog[] = outputs;

    if (filterFolder && filterFolder.length > 0) {
        data = data.filter((file: FileLog) => {
            const path = file.path ?? '';
            const segments = path.split(BaseCommon.CHARACTER_SLASH);
            const depth = segments.length;
            const format = isCSVFormat ? PATH_INDEX.CSV : PATH_INDEX.DB3;
            const isRootLevel = depth === format.FILEROOT_LEVEL;
            if (isRootLevel) {
                return true;
            }
            const logFolderIndex = format.LOG_FOLDER;
            const targetFolderIndex = format.TARGET_FOLDER;
            const isCorrectDepth = depth === format.STRICT_LEVEL;
            const isLogFolder = isCSVFormat
                ? EquipmentCommon.REGEX_LOG_FOLDER_CSV.test(segments[logFolderIndex])
                : segments[logFolderIndex] === EquipmentCommon.LOG_FOLDER;
            const isMatchedFolder = filterFolder.includes(segments[targetFolderIndex]);
            if (!(isCorrectDepth && isLogFolder && isMatchedFolder)) {
                return false;
            }
            const size = file.size;
            const fileType = isCSVFormat ? EFileExtensionUpload.CSV : EFileExtensionUpload.DB3;
            const isValidSize = size >= FileSupport[fileType].getMinSizeInBytes() && size < FileSupport[fileType].getMaxSizeInBytes();
            return isValidSize;
        });
    }

    if (filterFileExtensions?.length) {
        data = data.filter(({ name }: FileLog) => {
            const extension = name.slice(Math.max(name.lastIndexOf(TextCommon.DOT), ZERO_NUMBER));
            return filterFileExtensions.includes(extension);
        });
    }
    return data;
};

const readEquipmentId = async (inputs: FileLog[], originalFiles: FileLog[]): Promise<string> => {
    let isFileJSON = false;
    const equipmentInfoFile = inputs.find((file: FileLog) => {
        const isMatchFilename = EquipmentCommon.ROOT_FILE === file.name || EquipmentCommon.ROOT_FILE_JSON === file.name;
        if (isMatchFilename && file.name.endsWith(EFileExtensionCsv.JSON)) {
            isFileJSON = true;
        }
        const isFileRootLevel =
            file.path?.split(BaseCommon.CHARACTER_SLASH).length === (isFileJSON ? PATH_INDEX.CSV.FILEROOT_LEVEL : PATH_INDEX.DB3.FILEROOT_LEVEL);
        return isMatchFilename && isFileRootLevel;
    });

    if (!equipmentInfoFile) {
        throw new Error(MessageCommon.NOT_FOUND_EQUIPMENT_FILE);
    }

    let equipmentInfo;
    const content = await readContentFile(equipmentInfoFile);

    if (!content) {
        throw new Error(MessageCommon.INVALID_FILE);
    }
    if (isFileJSON) {
        try {
            const jsonContent = JSON.parse(content);
            equipmentInfo = jsonContent[EquipmentCommon.EQUIPMENT_ID_KEY];
        } catch (error) {
            throw new Error(MessageCommon.INVALID_FILE);
        }
    } else {
        equipmentInfo = content.match(EquipmentCommon.REGEX_EQUIPMENT_ID);
    }

    const invalidEquipmentId = isFileJSON ? !equipmentInfo : !equipmentInfo || !Array.isArray(equipmentInfo) || equipmentInfo.length !== 2;
    const isLogFileExist = originalFiles.filter((file: FileLog) => {
        const regexFile = isFileJSON ? EquipmentCommon.REGEX_CSV_FILE : EquipmentCommon.REGEX_DB3_FILE;
        const isMatchRegex = file.name.match(regexFile);
        return isMatchRegex;
    });
    const fileType = isFileJSON ? EquipmentCommon.ROOT_FILE_JSON : EquipmentCommon.ROOT_FILE;
    if (isLogFileExist.length === 0) {
        throw new Error(MessageCommon.NOT_GET_EQUIPMENT_LOG_FORMAT);
    }
    if (invalidEquipmentId) {
        throw new Error(format(MessageCommon.NOT_GET_EQUIPMENT_ID, fileType));
    }

    return isFileJSON ? equipmentInfo : equipmentInfo[1];
};

const readContentFile = async (input: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        if (!input) {
            reject(MessageCommon.INVALID_FILE);
        }

        const reader = new FileReader();
        reader.readAsText(input, 'UTF-8');
        reader.onload = function (evt: any): void {
            const result = evt.target.result;
            resolve(typeof result === 'string' ? result : JSON.stringify(result));
        };
        reader.onerror = function (evt: any): void {
            reject(JSON.stringify(evt));
        };
    });
};

export { readContentFile, readEquipmentId, validationFile, filterFileAndFolder, handleDirectoryEntry };
